import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, EllipsisVertical, LoaderCircle, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useIsMine } from '@/_shared/hooks/useIsMine';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { isOrpcForbiddenError, isOrpcNotFoundError } from '@/_shared/lib/orpc-errors';
import type { SSEMessage } from '@/_shared/types';

import { ChatArtifactsMenu } from '../components/artifacts/ChatArtifactsMenu';
import { ArtifactPanel, DashboardPreviewMobileSheet } from '../components/artifacts/DashboardPreview';
import { DashboardPreviewSlideShell } from '../components/artifacts/DashboardPreviewSlideShell';
import { ChatDownloadMenu } from '../components/ChatDownloadMenu';
import { ChatSharePopover, type Visibility } from '../components/ChatSharePopover';
import { PendingQuestion } from '../components/interactive/PendingQuestion';
import { MessageInput } from '../components/MessageInput';
import { MessageList } from '../components/MessageList';
import { ConnectWarehousePrompt } from '../components/onboarding/ConnectWarehousePrompt';
import { ReadOnlyChatNotice } from '../components/ReadOnlyChatNotice';
import { RenameChatDialog } from '../components/RenameChatDialog';
import { groupMessages } from '../components/tool-call-meta';
import { ChatSendContext, ComposerPrefillContext } from '../contexts/composer-prefill';
import { derivePendingBlock } from '../hooks/sse-reducer';
import { pendingAssistTurnSeq, useAssistReadback } from '../hooks/useAssistReadback';
import {
  isArtifactContentEmpty,
  isArtifactDraftForDisplay,
  isArtifactPersisted,
  useChatDashboardArtifacts,
} from '../hooks/useChatDashboardArtifacts';
import { useChatFeedback } from '../hooks/useChatFeedback';
import { useChatRename } from '../hooks/useChatRename';
import { useDashboardArtifactAutoSave } from '../hooks/useDashboardArtifactAutoSave';
import { useEndedStreamHistory } from '../hooks/useEndedStreamHistory';
import { useInteractiveBlockResolver } from '../hooks/useInteractiveBlockResolver';
import { usePreviewPanelSlide } from '../hooks/usePreviewPanelSlide';
import { useSSE } from '../hooks/useSSE';
import { useTurnStop } from '../hooks/useTurnStop';
import { useWarehouseGate } from '../hooks/useWarehouseGate';
import { adoptDashboardToolResults } from '../lib/adopt-dashboard-tool-results';
import {
  adoptableRouterTurn,
  createAiTurnReporter,
  type PendingTurnRouterState,
  type PromptSource,
  sseTerminalOutcome,
  turnStatusOutcome,
} from '../lib/ai-turn-events';
import { applyChatTitle, clearTitleRetry, refetchChatTitle, TITLE_READBACK_RETRY_MS } from '../lib/apply-chat-title';
import { collectChatArtifacts, withStoreOnlyDashboards } from '../lib/chat-artifacts';
import { buildDashboardSavedSlugsPrefix } from '../lib/dashboard-activity';
import { type DashboardArtifactOpenHint, restoreDashboardArtifactsFromMessages } from '../lib/dashboard-draft';
import { extractErrorMessage } from '../lib/extract-error';
import { openDashboardArtifactFromHint } from '../lib/open-dashboard-artifact';
import { resolveDashboardDraftForAgent } from '../lib/resolve-dashboard-draft-for-agent';
import {
  completedTurnToolUseIds,
  dropUnfinishedTrailingTurn,
  filterOutTurn,
  isTurnTerminalInHistory,
  mergeHistoryWithLiveMessages,
  sliceIntoTurns,
} from '../lib/turn-slicing';

interface ChatConversationProps {
  sessionId: string;
}

export function ChatConversation({ sessionId }: ChatConversationProps): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { slug, wp, workspace } = useWorkspace();
  const orpc = useOrpc();
  const dashboardArtifactsStore = useChatDashboardArtifacts();
  const isMobile = useIsMobile();
  const [splitEverOpened, setSplitEverOpened] = useState(false);

  const {
    data: sessionData,
    error: sessionError,
    isPending: sessionPending,
    refetch: refetchSession,
  } = useQuery(orpc.chat.get.queryOptions({ input: { id: sessionId } }));

  // `chat.get` admits a non-owner for a workspace-visible chat, but every write procedure is
  // owner-only — so gate every write affordance on ownership (ACT2-589). The comparison IS the
  // server's own rule: it only ever returns a session from the caller's workspace, so matching the
  // owner id is all that's left of it.
  const isMine = useIsMine();
  const readOnly = sessionData ? !isMine(sessionData.owner.id) : false;
  const chatFeedback = useChatFeedback(sessionId);
  const refreshChatFeedback = chatFeedback.refresh;
  // Writes fail closed while ownership is still unknown. `readOnly` stays false during load so the
  // render and streaming paths behave exactly as before, but a hook that runs BEFORE the pending
  // early-return below must not treat "not yet answered" as "allowed".
  const canWrite = sessionData !== undefined && !readOnly;
  const boundSession = sessionData?.session;
  const isArtifactBound = Boolean(boundSession?.dashboardId ?? boundSession?.workflowId ?? boundSession?.audienceId);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  /** De-dupes the agent's `change_title` messages. Per-session — cleared when the session changes. */
  const lastTitleChangeRef = useRef<string | null>(null);
  /** The delayed title read-back scheduled at turn end. Cleared once a name arrives some other way. */
  const titleRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renameMutation = useChatRename();

  const { cancelPending: cancelDashboardAutoSave } = useDashboardArtifactAutoSave(
    dashboardArtifactsStore.artifacts,
    dashboardArtifactsStore,
    sessionId,
    { enabled: canWrite },
  );

  const routerState = location.state as PendingTurnRouterState | null;
  const routerTurnId = routerState?.pendingTurnId ?? null;

  const { data: skillsData } = useQuery(orpc.skills.listInvokable.queryOptions({ input: {} }));
  const hasInvokableSkills = (skillsData?.skills ?? []).some((skill) => skill.enabled);

  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  // An aborted prompt only comes back into an empty composer; a card's "Try it" replaces the draft.
  const [prefillReplace, setPrefillReplace] = useState(false);
  const restoreToComposer = useCallback((content: string): void => {
    setPrefillReplace(false);
    setPrefillValue(content);
    setPrefillNonce((n) => n + 1);
  }, []);
  const fillComposer = useCallback((prompt: string): void => {
    setPrefillReplace(true);
    setPrefillValue(prompt);
    setPrefillNonce((n) => n + 1);
  }, []);

  const sendMessageMutation = useMutation(
    orpc.chat.sendMessage.mutationOptions({
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: variables.id } }).queryKey,
        });
      },
    }),
  );

  const aiTurns = useMemo(
    () => createAiTurnReporter({ accountId: workspace.id, feature: 'web_chat', surface: 'web' }),
    [workspace.id],
  );
  // gatedSend carries no source: each entry point stamps the one send it starts.
  const nextPromptSourceRef = useRef<PromptSource>('composer');
  // A new chat's first turn: ChatWelcome sent its prompt copy, this view sends its answer copy.
  useEffect(() => {
    const turn = adoptableRouterTurn(routerState);
    if (turn) aiTurns.adopt(turn.turnId, sessionId, turn.submittedAt);
  }, [aiTurns, routerState, sessionId]);

  // A turn that ended before its stream opened is never streamed: its terminal event is in `chat.get`.
  useEffect(() => {
    let lastTurnId: string | undefined;
    for (const message of sessionData?.sseMessages ?? []) {
      if (message.type === 'turn_start') lastTurnId = message.turnId;
      if (message.type !== 'turn_complete' && message.type !== 'turn_aborted' && message.type !== 'error') continue;
      const turnId = message.turnId ?? (message.type === 'error' ? lastTurnId : undefined);
      if (turnId) aiTurns.finished(turnId, sseTerminalOutcome(message.type, message));
    }
  }, [aiTurns, sessionData?.sseMessages]);

  // pendingTurnId bridges the gap between mutation response and SSE delivery.
  // Set from sendMessage response OR from router state (welcome screen flow).
  // Cleared when the turn completes in the SSE stream.
  const [pendingTurnId, setPendingTurnId] = useState<string | null>(routerTurnId);

  // A viewer streams too (the SSE route applies the same read rule as `chat.get`), but only ever the
  // owner's turn — they have no `pendingTurnId` of their own to contribute.
  const knownTurnId = readOnly
    ? (sessionData?.runtime?.activeTurn?.turnId ?? null)
    : (pendingTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null);

  // Only stream a turn that hasn't already finished in persisted history. The per-turn SSE endpoint
  // replays every persisted event for the turn, so connecting to a completed turn — e.g. a stale
  // router `pendingTurnId` after SPA navigate-back — would re-deliver rows already in `chat.get` and,
  // once the turn is excluded from history by `filterOutTurn`, append them out of order. A `null`
  // history (welcome → first-message bridge) means the turn is brand-new: stream it.
  const activeStreamTurnId = useMemo(() => {
    if (!knownTurnId) return null;
    const history = sessionData?.sseMessages;
    if (!history) return knownTurnId;
    return isTurnTerminalInHistory(history, knownTurnId) ? null : knownTurnId;
  }, [knownTurnId, sessionData?.sseMessages]);

  const streamUrl =
    activeStreamTurnId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${sessionId}/turns/${activeStreamTurnId}/events`
      : null;

  const turnStop = useTurnStop({
    workspaceKey: slug,
    sessionId,
    activeTurnId: activeStreamTurnId,
    stopCall: (input) => orpc.chat.stopTurn.call(input),
    // A stopped prompt stays in the transcript, marked "Stopped" (the abort marker), in every tab and
    // after a reload. The composer is never touched, so a draft typed during the turn stays put.
    onConfirmed: (turnId, status) => {
      // Other durable statuses lack the SDK verdict; the history refetch below supplies it.
      if (status === 'aborted') aiTurns.finished(turnId, turnStatusOutcome(status));
      setPendingTurnId(null);
      dashboardArtifactsStore.clearAllServerPersistPending();
      void queryClient.invalidateQueries({
        queryKey: orpc.chat.get.queryOptions({ input: { id: sessionId } }).queryKey,
      });
    },
  });
  const { noteTerminal, noteStreamEnded } = turnStop;

  /** Set once `useSSE` has returned; the stream callback below runs only after that. */
  const assistReadbackRef = useRef<((afterSeq: number) => void) | null>(null);

  const sseOptions = useMemo(
    () => ({
      scopeKey: sessionId,
      turnId: activeStreamTurnId,
      recovery: {
        sessionId,
        persistedMessages: sessionData?.sseMessages,
        runtimeTurnId: sessionData?.runtime?.activeTurn?.turnId ?? null,
        refetchSession,
        controlsEnabled: !readOnly,
      },
      onEvent: (eventType: string, data: Record<string, unknown>) => {
        if (eventType === 'turn_complete' || eventType === 'turn_aborted' || eventType === 'error') {
          const endedTurnId = typeof data['turnId'] === 'string' ? data['turnId'] : activeStreamTurnId;
          if (endedTurnId) {
            noteTerminal(endedTurnId);
            aiTurns.finished(endedTurnId, sseTerminalOutcome(eventType, data));
          }
          // A rollout read may have reported no local runtime while this stream was still healthy.
          // Refresh the saved terminal state for every chat so that stale snapshot cannot close it.
          void queryClient.invalidateQueries({
            queryKey: orpc.chat.get.queryOptions({ input: { id: sessionId } }).queryKey,
          });
          if (eventType === 'turn_complete') refreshChatFeedback();
          // The server may still be picking a setup card for this turn; the stream has closed, so
          // read it back (`useAssistReadback`).
          if (eventType === 'turn_complete' && data['assistPending'] === true && typeof data['seq'] === 'number') {
            assistReadbackRef.current?.(data['seq']);
          }
        }
        // A question or approval shows as PendingQuestion, with no ui_message.
        const answerShown =
          (eventType === 'ui_message' && data['uiType'] !== 'change_title' && data['uiType'] !== 'recommend_actions') ||
          eventType === 'awaiting_input';
        if (answerShown && activeStreamTurnId) aiTurns.rendered(activeStreamTurnId);
        if (eventType === 'dashboard_draft') {
          dashboardArtifactsStore.applyAgentDraft(data);
          return;
        }
        // A brand-new thread has just been named. The event carries the name, so patch both caches
        // rather than refetching: `chat.get` would re-download the whole transcript for one string.
        if (eventType === 'chat_title') {
          const title = typeof data['content'] === 'string' ? data['content'] : null;
          if (title !== null) {
            clearTitleRetry(titleRetryTimerRef);
            applyChatTitle(queryClient, orpc, sessionId, title);
          }
          return;
        }
      },
    }),
    [
      dashboardArtifactsStore,
      queryClient,
      orpc,
      sessionId,
      activeStreamTurnId,
      sessionData,
      refetchSession,
      readOnly,
      noteTerminal,
      refreshChatFeedback,
      aiTurns,
    ],
  );

  const {
    messages: sseMessages,
    error: sseError,
    ended: sseEnded,
    endedTurnId,
    recovered: streamHistoryRecovered,
    recoveryStatus,
    retryRecovery,
    recoverHistory,
    clearMessages,
    addMessage,
    insertMessage,
    reconnect,
    removeLastUserMessage,
    initializeMessages,
  } = useSSE(streamUrl, sseOptions);
  useEndedStreamHistory({
    sessionId,
    turnId: endedTurnId,
    ended: sseEnded && !streamHistoryRecovered,
    recoverHistory,
  });

  const deleteMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        void navigate(wp('/chat'));
      },
    }),
  );

  const visibilityMutation = useMutation(
    orpc.chat.updateVisibility.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: sessionId } }).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err: { message?: string }) => {
        toast.error(err.message ?? 'Failed to update chat visibility');
      },
    }),
  );

  const slices = useMemo(() => sliceIntoTurns(sseMessages), [sseMessages]);
  const activeSlice = slices.find((s) => s.status === 'active') ?? null;
  const runtimeTurnId =
    sessionData?.session.id === sessionId ? (sessionData.runtime?.activeTurn?.turnId ?? null) : null;
  const runtimeBusy =
    runtimeTurnId !== null &&
    activeStreamTurnId === runtimeTurnId &&
    runtimeTurnId !== turnStop.confirmedTurnId &&
    !isTurnTerminalInHistory(sseMessages, runtimeTurnId);
  const active =
    (((activeSlice !== null && activeSlice.turnId !== turnStop.confirmedTurnId) ||
      (pendingTurnId !== null && pendingTurnId !== turnStop.confirmedTurnId) ||
      runtimeBusy) &&
      !sseEnded) ||
    sendMessageMutation.isPending ||
    turnStop.blocking;
  const isRecoveringTurn = recoveryStatus !== null && !sseEnded;

  useEffect(() => {
    if (sseEnded && knownTurnId) noteStreamEnded(knownTurnId);
  }, [sseEnded, knownTurnId, noteStreamEnded]);

  // Interactive blocks (AI-411): the question/approval block currently gating the composer, if any.
  const pendingBlock = useMemo(() => derivePendingBlock(sseMessages), [sseMessages]);
  const {
    resolveBlock: handleResolveBlock,
    submittingBlockId,
    composerFocusNonce,
  } = useInteractiveBlockResolver({ sessionId, addMessage, reconnect });

  // Clear pendingTurnId once the matching turn completes (or errors) in SSE.
  useEffect(() => {
    if (!pendingTurnId) return;
    const match = slices.find((s) => s.turnId === pendingTurnId);
    if (match && match.status !== 'active') {
      // Syncs local state to the asynchronous SSE message stream (an external system owned by
      // useSSE); slices is derived from sseMessages which only changes when the stream delivers
      // an event, so this can't be observed via a synchronous event handler.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears pendingTurnId once the matching turn completes in the async SSE stream (external system)
      setPendingTurnId(null);
    }
  }, [slices, pendingTurnId]);

  // A dead SSE connection no longer owns the local bridge ID. useTurnStop keeps
  // Send blocked and the dashboard persistence markers intact until Postgres
  // confirms a terminal turn, even if the stream cannot resume.
  useEffect(() => {
    if (pendingTurnId && sseEnded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async stream failure clears only the local bridge ID
      setPendingTurnId(null);
    }
  }, [pendingTurnId, sseEnded]);

  // Initialize SSE messages from historical data (once per session). Race-safe:
  // if the SSE stream has already delivered events for the active turn before
  // chat.get returns, we prepend historical messages instead of replacing.
  const initializedSessionRef = useRef<string | null>(null);
  const processedDashboardToolIdsRef = useRef<Set<string>>(new Set());
  const cardOpenRequestIdRef = useRef(0);
  const sseMessagesRef = useRef(sseMessages);
  useEffect(() => {
    sseMessagesRef.current = sseMessages;
  });
  const { readBack: readBackAssist, cancel: cancelAssistReadback } = useAssistReadback({
    sessionId,
    messagesRef: sseMessagesRef,
    insertMessage,
  });
  useEffect(() => {
    assistReadbackRef.current = readBackAssist;
  }, [readBackAssist]);

  useEffect(() => {
    if (sessionData?.sseMessages && initializedSessionRef.current !== sessionId) {
      initializedSessionRef.current = sessionId;
      // A legacy trailing turn can lack a durable active row. Without a stream, stop the viewer's
      // transcript at its last completed turn. With a stream, `filterOutTurn` handles the active turn.
      const history =
        readOnly && !activeStreamTurnId ? dropUnfinishedTrailingTurn(sessionData.sseMessages) : sessionData.sseMessages;
      // Exclude only a turn we will actually stream live (active + not terminal in history). A stale
      // completed turn stays in history so it renders in chronological order, not re-appended by SSE.
      const filtered = activeStreamTurnId ? filterOutTurn(history, activeStreamTurnId) : history;
      const currentSSE = sseMessagesRef.current;

      if (currentSSE.length > 0) {
        // Dedup by seq — a completed turn can sit in both history and the live buffer (it finished
        // before chat.get returned, so filterOutTurn didn't strip it), which would render it twice.
        initializeMessages(mergeHistoryWithLiveMessages(filtered, currentSSE));
      } else {
        initializeMessages(filtered);
      }

      // Pre-mark completed-turn tools only — the streamed active turn is excluded so its tool_result
      // (streamed live via SSE, not yet in history) still gets adopted. See `completedTurnToolUseIds`.
      for (const id of completedTurnToolUseIds(sessionData.sseMessages, activeStreamTurnId)) {
        processedDashboardToolIdsRef.current.add(id);
      }

      // The chat was opened after its last turn ended but before that turn's assist card was written:
      // no stream will replay the `turn_complete`, so read the card back from here.
      const pendingAssistSeq = pendingAssistTurnSeq(history, Date.now());
      if (pendingAssistSeq !== null) readBackAssist(pendingAssistSeq);

      const restored = restoreDashboardArtifactsFromMessages(history);
      if (Object.keys(restored).length > 0) {
        // Active stream → a turn is still streaming (reload mid-turn); the merge must not close a
        // watched panel or reset the turn/dedup refs out from under the live SSE stream.
        dashboardArtifactsStore.restoreFromHistory(restored, { hasActiveTurn: activeStreamTurnId !== null });
      }
    }
  }, [
    sessionData?.sseMessages,
    sessionId,
    readOnly,
    activeStreamTurnId,
    initializeMessages,
    dashboardArtifactsStore,
    readBackAssist,
  ]);

  // Clear messages when switching sessions within /chat/:id navigations.
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (sessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = sessionId;
      clearMessages();
      cancelDashboardAutoSave();
      dashboardArtifactsStore.discard();
      setSplitEverOpened(false);
      processedDashboardToolIdsRef.current.clear();
      // Invalidate any in-flight card-open fetch from the previous session so its late resolve can't
      // hydrate an artifact into the new session (isCurrentRequest would otherwise still match).
      cardOpenRequestIdRef.current += 1;
      setPendingTurnId(routerTurnId);
      initializedSessionRef.current = null;
      setPrefillValue(undefined);
      setPrefillNonce(0);
      setPrefillReplace(false);
      // Without this, leaving a chat with the rename dialog open leaves the id set, and coming back
      // to that chat reopens the dialog on its own.
      setRenamingId(null);
      clearTitleRetry(titleRetryTimerRef);
      // The de-dupe ref is per-session too: two new chats opened with the same short first message
      // get the same name, and a stale ref would swallow the second one's refresh.
      lastTitleChangeRef.current = null;
    }
  }, [sessionId, routerTurnId, clearMessages, dashboardArtifactsStore, cancelDashboardAutoSave]);

  // React to change_title ui_messages by re-reading the title. NOT by trusting the message content:
  // `sseMessages` holds the persisted transcript too, so an old `change_title` replays on every load
  // and would write the agent's stale name over one the owner has since chosen. Only the live
  // `chat_title` event, which never replays, patches the caches from its content.
  useEffect(() => {
    // The effect also runs while a switch is in flight, when `sseMessages` still holds the chat
    // being left. Wait until this session's own transcript is in.
    if (initializedSessionRef.current !== sessionId) return;
    let titleMsg: SSEMessage | undefined;
    for (let i = sseMessages.length - 1; i >= 0; i--) {
      const m = sseMessages[i]!;
      if (m.type === 'ui_message' && m.uiType === 'change_title') {
        titleMsg = m;
        break;
      }
    }
    if (titleMsg?.type === 'ui_message' && titleMsg.content !== lastTitleChangeRef.current) {
      lastTitleChangeRef.current = titleMsg.content;
      refetchChatTitle(queryClient, orpc, sessionId);
    }
  }, [sseMessages, queryClient, sessionId, orpc]);

  // A name that lands after the turn has torn down reaches no listener, so the ephemeral `chat_title`
  // event is lost and the header would sit on "New conversation" until the user navigates away. Read
  // it back once per turn end, and only while the thread is still nameless.
  const titleBackfilledTurnRef = useRef<string | null>(null);
  useEffect(() => {
    const last = sseMessages[sseMessages.length - 1];
    if (!last || (last.type !== 'turn_complete' && last.type !== 'error' && last.type !== 'turn_aborted')) return;
    if (sessionData?.session?.title !== null) return;
    // A `turn_aborted` whose persistence failed carries no seq, so fall back to the transcript length:
    // two such turns in one session must not collapse to one marker and skip the second read-back.
    const marker = `${sessionId}:${last.seq ?? sseMessages.length}`;
    if (titleBackfilledTurnRef.current === marker) return;
    titleBackfilledTurnRef.current = marker;
    refetchChatTitle(queryClient, orpc, sessionId);
    // The titler may still be running: an `error` or a Stop can end a turn in under a second, before
    // the name is written. Read back once more after the titler's own window has passed.
    clearTitleRetry(titleRetryTimerRef);
    titleRetryTimerRef.current = setTimeout(() => {
      titleRetryTimerRef.current = null;
      refetchChatTitle(queryClient, orpc, sessionId);
    }, TITLE_READBACK_RETRY_MS);
  }, [sseMessages, sessionData?.session, queryClient, sessionId, orpc]);

  useEffect(() => () => clearTitleRetry(titleRetryTimerRef), []);

  usePageTitle(sessionData?.session?.title);

  const handleSend = useCallback(
    async (content: string, invokedSkillIds?: string[]): Promise<void> => {
      // Guard here, not only at the controls: every write affordance is already hidden in read-only,
      // but a future caller wiring into onAction/onAskAssistant must not reopen the hole (ACT2-589).
      if (readOnly || isRecoveringTurn || turnStop.blocking) return;
      const submittedAt = performance.now();
      const promptSource = nextPromptSourceRef.current;
      nextPromptSourceRef.current = 'composer';
      // Add optimistic user_message — will be deduped when the SSE stream
      // delivers the persisted copy with a seq number.
      addMessage({ type: 'user_message', content });
      try {
        const dashboardDraft = await resolveDashboardDraftForAgent(dashboardArtifactsStore, async (slug) => {
          try {
            const result = await queryClient.fetchQuery(orpc.dashboard.get.queryOptions({ input: { slug } }));
            return result.dashboard;
          } catch (err) {
            if (isOrpcNotFoundError(err)) return null;
            throw err;
          }
        });

        const variables = {
          id: sessionId,
          content,
          invokedSkillIds: invokedSkillIds ?? [],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          dashboardDraft,
          dashboardArtifactsInventory: dashboardArtifactsStore.inventorySnapshot(),
          clientRequestId: crypto.randomUUID(),
        };
        const result = await sendMessageMutation.mutateAsync(variables);
        if (result.turnId) {
          // The turnId is the interaction id; a failed send has none, so it sends no AI events.
          aiTurns.submitted(result.turnId, sessionId, submittedAt, promptSource, content.length);
          // A new turn started: the server writes no card for the last one from here on, so stop reading
          // it back. Only now: a send that fails leaves the last turn's read-back running.
          cancelAssistReadback();
          setPendingTurnId(result.turnId);
        }
      } catch (err: unknown) {
        // TURN_IN_PROGRESS: another turn is already running (post-stop race).
        // Remove the optimistic bubble and restore the prompt so the user can
        // retry once the abort completes.
        const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
        if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
          removeLastUserMessage(content);
          restoreToComposer(content);
          setPendingTurnId(errData.activeTurnId);
          return;
        }
        removeLastUserMessage(content);
        addMessage({ type: 'error', message: extractErrorMessage(err) });
      }
    },
    [
      readOnly,
      isRecoveringTurn,
      turnStop.blocking,
      sessionId,
      sendMessageMutation,
      addMessage,
      removeLastUserMessage,
      restoreToComposer,
      dashboardArtifactsStore,
      queryClient,
      orpc.dashboard.get,
      aiTurns,
      cancelAssistReadback,
    ],
  );

  // ACT2-690: every send below goes through the gate, so a message sent while the workspace has no
  // warehouse waits behind the connect prompt instead of starting a turn that cannot answer it.
  const gate = useWarehouseGate(handleSend, { restore: restoreToComposer, scope: sessionId, bypass: active });

  const handleDelete = useCallback(() => {
    if (readOnly) return;
    deleteMutation.mutate({ id: sessionId });
  }, [readOnly, sessionId, deleteMutation]);

  const handleUpdateVisibility = useCallback(
    (visibility: Visibility) => {
      if (readOnly) return;
      visibilityMutation.mutate({ id: sessionId, visibility });
    },
    [readOnly, sessionId, visibilityMutation],
  );

  const session = sessionData?.session ?? null;
  // Demo/challenge-workspace budget stop (ACT2 self-serve sandbox): the thread is over once a turn hit
  // the SDK's budget cap — chat.sendMessage refuses further turns server-side; this just makes that
  // true here too instead of only surfacing as a failed-send toast.
  const budgetExhausted = session?.budgetExhausted ?? false;

  const visibility: Visibility =
    session?.visibility === 'workspace' || session?.visibility === 'public' || session?.visibility === 'private'
      ? session.visibility
      : 'private';
  const loadedSkills = useMemo(() => {
    const ids = session?.loadedSkillIds ?? [];
    const messages = sessionData?.sseMessages ?? [];
    const skills = skillsData?.skills ?? [];
    // One reverse pass for invoke-time title snapshots — avoid O(skills × messages) per render.
    const titleByInvokedId = new Map<string, string>();
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.type === 'skill_invoked' && !titleByInvokedId.has(message.skillId)) {
        titleByInvokedId.set(message.skillId, message.title);
      }
    }
    return ids.map((id) => ({
      id,
      title: skills.find((skill) => skill.id === id)?.name ?? titleByInvokedId.get(id) ?? 'Deleted skill',
    }));
  }, [session?.loadedSkillIds, sessionData?.sseMessages, skillsData?.skills]);

  const hasActiveArtifact = !!dashboardArtifactsStore.activeArtifact;
  const panelOpen = hasActiveArtifact && dashboardArtifactsStore.panelOpen;
  const previewSlide = usePreviewPanelSlide(panelOpen && !isMobile);
  const previewOpenerRef = useRef<HTMLElement | null>(null);
  const wasPreviewOpenRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- latch split layout once preview opens so close animation keeps split chrome
    if (panelOpen && !isMobile) setSplitEverOpened(true);
  }, [panelOpen, isMobile]);

  useEffect(() => {
    if (panelOpen) {
      if (!wasPreviewOpenRef.current && document.activeElement instanceof HTMLElement) {
        previewOpenerRef.current = document.activeElement;
      }
      wasPreviewOpenRef.current = true;
      return;
    }
    if (!wasPreviewOpenRef.current) return;
    wasPreviewOpenRef.current = false;
    const opener = previewOpenerRef.current;
    previewOpenerRef.current = null;
    if (opener && typeof opener.focus === 'function') {
      queueMicrotask(() => opener.focus());
    }
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      dashboardArtifactsStore.closePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [panelOpen, dashboardArtifactsStore]);

  // Adopt completed preview/save tool results into the live artifact store. The agent may
  // call save_dashboard without preview_dashboard; auto-save can also outpace transcript
  // persistence — keep the card/panel aligned with what was actually saved.
  // Depend on message list only — store methods are stable enough via ref to avoid
  // re-scanning on every artifact mutation.
  const dashboardArtifactsStoreRef = useRef(dashboardArtifactsStore);
  useLayoutEffect(() => {
    dashboardArtifactsStoreRef.current = dashboardArtifactsStore;
  }, [dashboardArtifactsStore]);
  useEffect(() => {
    const newlyProcessed = adoptDashboardToolResults(
      sseMessages,
      dashboardArtifactsStoreRef.current,
      processedDashboardToolIdsRef.current,
    );
    for (const id of newlyProcessed) processedDashboardToolIdsRef.current.add(id);
  }, [sseMessages]);

  const useSplitLayout = splitEverOpened && !isMobile;

  const handlePreviewOpenChange = useCallback(
    (open: boolean) => {
      if (!open) dashboardArtifactsStore.closePanel();
    },
    [dashboardArtifactsStore],
  );

  const handleDashboardArtifactOpen = useCallback(
    (slug: string, hint?: DashboardArtifactOpenHint) => {
      const requestId = ++cardOpenRequestIdRef.current;
      const existing = dashboardArtifactsStore.artifacts[slug];
      void openDashboardArtifactFromHint(slug, hint, existing, () => requestId === cardOpenRequestIdRef.current, {
        hydrateSavedAndOpen: dashboardArtifactsStore.hydrateSavedAndOpen,
        hydrateNotFoundAndOpen: dashboardArtifactsStore.hydrateNotFoundAndOpen,
        setActive: dashboardArtifactsStore.setActive,
        fetchDashboard: async (s) => {
          const result = await queryClient.fetchQuery(orpc.dashboard.get.queryOptions({ input: { slug: s } }));
          return { id: result.dashboard.id, title: result.dashboard.title };
        },
      });
    },
    [dashboardArtifactsStore, queryClient, orpc.dashboard.get],
  );

  const resolveDashboardArtifact = useCallback(
    (slug: string) => {
      const artifact = dashboardArtifactsStore.artifacts[slug];
      if (!artifact) return null;
      return {
        title: artifact.title,
        saved: artifact.saved !== null && !artifact.externallyRemoved,
        isDraft: artifact.saved === null && !artifact.externallyRemoved,
        // Omit rather than publish `0` for an un-hydrated shell, so a real empty spec stays distinguishable
        // from "not loaded yet" and the consumer needs no falsy-zero guard.
        ...(isArtifactContentEmpty(artifact) ? {} : { panelCount: artifact.spec.panels.length }),
      };
    },
    [dashboardArtifactsStore.artifacts],
  );

  // Derived HERE, not in MessageList: this component already owns `sseMessages` and the live store, so one
  // pass feeds both the transcript cards and the header menu with no child→parent state to go stale on a
  // session switch.
  // A stopped prompt is the owner's: a viewer's transcript leaves stopped turns out, as `chat.get` does
  // for history. This also covers an abort the viewer receives live. The same array feeds the grouping,
  // the saved-slug prefix and the list, so their indexes stay aligned.
  const transcriptMessages = useMemo(
    () => (readOnly ? sseMessages.filter((m) => m.type !== 'turn_aborted') : sseMessages),
    [readOnly, sseMessages],
  );
  const groupedMessages = useMemo(() => groupMessages(transcriptMessages), [transcriptMessages]);
  const savedSlugsPrefix = useMemo(() => buildDashboardSavedSlugsPrefix(transcriptMessages), [transcriptMessages]);
  const chatArtifacts = useMemo(
    () =>
      withStoreOnlyDashboards(
        collectChatArtifacts(groupedMessages, { savedSlugsPrefix, resolveDashboardArtifact }),
        dashboardArtifactsStore.artifactList.map((artifact) => ({
          slug: artifact.slug,
          title: artifact.title,
          saved: artifact.saved !== null && !artifact.externallyRemoved,
          isDraft: isArtifactDraftForDisplay(artifact),
          ...(artifact.saved?.id ? { dashboardId: artifact.saved.id } : {}),
          ...(isArtifactContentEmpty(artifact) ? {} : { panelCount: artifact.spec.panels.length }),
        })),
      ),
    [groupedMessages, savedSlugsPrefix, resolveDashboardArtifact, dashboardArtifactsStore.artifactList],
  );

  const activeArtifactPersisted =
    dashboardArtifactsStore.activeArtifact !== null && isArtifactPersisted(dashboardArtifactsStore.activeArtifact);

  const hideSaveDashboardSuggestions =
    activeArtifactPersisted && !dashboardArtifactsStore.artifactList.some(isArtifactDraftForDisplay);

  const ownerName = sessionData?.owner.name ?? null;

  // Hold the composer slot until `chat.get` resolves, exactly as the header does. Rendering a
  // composer against unknown ownership would flash a working input at a viewer who can never send —
  // the very thing this mode exists to stop.
  const questionPending = pendingBlock !== null && !isRecoveringTurn && !sseEnded;
  const composer = !sessionData ? null : (
    <div className="relative shrink-0">
      <div className="relative mx-auto w-full max-w-4xl px-6 py-4">
        {readOnly ? (
          // A viewer gets no composer at all — not a disabled one. There is no state in which they
          // could send, so a disabled input would be a permanent dead affordance.
          <ReadOnlyChatNotice ownerName={ownerName} />
        ) : gate.held && !questionPending ? (
          <ConnectWarehousePrompt
            held={gate.held}
            onConnected={() => void gate.release()}
            onSendAnyway={() => void gate.sendAnyway()}
            onDismiss={gate.dismiss}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {/* The active interactive block gates the turn: the user must answer, skip or dismiss it
                before the turn can go on. It sits above the composer rather than in its place, so a
                draft typed during the turn survives the question. Send stays unavailable until the
                turn finishes, and Stop stays in the composer. */}
            {questionPending ? (
              <PendingQuestion
                block={pendingBlock}
                onResolve={(answer) => handleResolveBlock(pendingBlock, answer)}
                submitting={submittingBlockId === pendingBlock.blockId}
              />
            ) : null}
            {sseEnded && sseError && !streamHistoryRecovered ? (
              <Callout variant="warning">{sseError}</Callout>
            ) : isRecoveringTurn ? (
              <Callout variant={recoveryStatus === 'recovering' ? 'info' : 'warning'}>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <span>
                    {recoveryStatus === 'recovering'
                      ? 'Reconnecting to this conversation…'
                      : 'This turn is unavailable. Try reconnecting, or start a new chat.'}
                  </span>
                  {recoveryStatus === 'unavailable' && (
                    <Button variant="secondary" size="sm" onClick={retryRecovery}>
                      Reconnect
                    </Button>
                  )}
                  <Button asChild variant="secondary" size="sm" className="shrink-0">
                    <Link to={wp()}>New chat</Link>
                  </Button>
                </div>
              </Callout>
            ) : budgetExhausted ? (
              <Callout variant="info">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <span>The budget for this thread is over. Start a new chat to continue exploring.</span>
                  <Button asChild variant="secondary" size="sm" className="shrink-0">
                    <Link to={wp()}>New chat</Link>
                  </Button>
                </div>
              </Callout>
            ) : null}
            <MessageInput
              embedded
              onSend={(content, invokedSkillIds) => {
                // The composer unmounts while a message is held, and re-applies its prefill when it
                // mounts again; text already sent must not come back.
                setPrefillValue(undefined);
                nextPromptSourceRef.current = 'composer';
                gate.gatedSend(content, invokedSkillIds);
              }}
              disabled={active || questionPending || budgetExhausted || isRecoveringTurn}
              // A running turn blocks Send and Enter, and leaves the field open so the next message
              // can be drafted. A closed or reconnecting thread still locks the field.
              locked={budgetExhausted || isRecoveringTurn}
              stopping={turnStop.stopping || turnStop.pending}
              stopStatusError={turnStop.statusError}
              stopStalled={turnStop.stopStalled}
              onRetryStopStatus={turnStop.retryStatus}
              // Override the generic disabled placeholder ("Loading..." / "Lookout is responding...") —
              // this thread isn't busy, it's over, and the Callout above already says why.
              {...(isRecoveringTurn
                ? {
                    placeholder: recoveryStatus === 'recovering' ? 'Reconnecting…' : 'Conversation unavailable',
                  }
                : budgetExhausted
                  ? { placeholder: 'This thread is closed' }
                  : {})}
              agentLoaded={activeStreamTurnId !== null || turnStop.blockingTurnId !== null}
              variant="chat"
              loadedSkills={loadedSkills}
              workspaceSlug={slug}
              hasInvokableSkills={hasInvokableSkills}
              onStop={
                (activeStreamTurnId ?? turnStop.blockingTurnId) &&
                (activeStreamTurnId ?? turnStop.blockingTurnId) !== turnStop.confirmedTurnId &&
                !turnStop.terminalSeen &&
                !turnStop.stopping &&
                !turnStop.pending
                  ? () => turnStop.stop((activeStreamTurnId ?? turnStop.blockingTurnId)!)
                  : undefined
              }
              prefillValue={prefillValue}
              prefillNonce={prefillNonce}
              prefillReplace={prefillReplace}
              // Held while a question card is open: a failed answer leaves the card up, and focus belongs
              // there. The composer takes focus once the card closes.
              autoFocusNonce={questionPending ? 0 : composerFocusNonce}
            />
          </div>
        )}
      </div>
    </div>
  );

  // An assist card's skill button sends through the same gate as the composer, and only while it could.
  const chatSend = useMemo(
    () =>
      canWrite && !active && !budgetExhausted && !isRecoveringTurn
        ? (content: string, invokedSkillIds?: string[]) => gate.gatedSend(content, invokedSkillIds)
        : null,
    [canWrite, active, budgetExhausted, isRecoveringTurn, gate],
  );

  const transcript = (
    <ComposerPrefillContext.Provider value={canWrite ? fillComposer : null}>
      <ChatSendContext.Provider value={chatSend}>
        <MessageList
          messages={transcriptMessages}
          suggestionSurface="chat"
          thinking={active && !pendingBlock && !isRecoveringTurn}
          onAction={(content) => {
            // A held message ignores the click, so it must keep its own source.
            if (!gate.held) nextPromptSourceRef.current = 'follow_up_action';
            gate.gatedSend(content);
          }}
          // Action chips send a message, which a viewer can't do. This is the gate that matters:
          // MessageList skips the block entirely, whereas RecommendedActions renders its buttons
          // whether or not it was given a handler.
          showRecommendActions={!readOnly}
          actionsDisabled={active || isRecoveringTurn}
          compact={panelOpen && !isMobile}
          dashboardSaved={hideSaveDashboardSuggestions}
          onDashboardArtifactOpen={handleDashboardArtifactOpen}
          resolveDashboardArtifact={resolveDashboardArtifact}
          grouped={groupedMessages}
          savedSlugsPrefix={savedSlugsPrefix}
          artifacts={chatArtifacts}
          feedback={{
            canRate: chatFeedback.canRate,
            eligibleTurnIds: chatFeedback.eligibleTurnIds,
            ratingByTurn: chatFeedback.ratingByTurn,
            isPending: chatFeedback.isPending,
            onRate: chatFeedback.setRating,
          }}
        />
        {composer}
      </ChatSendContext.Provider>
    </ComposerPrefillContext.Provider>
  );

  // A chat you can't read (never shared with you, or deleted) resolves to an error, not an empty
  // session — and with the composer gated on a loaded session there would otherwise be nothing on
  // screen at all. Mirrors the dashboard not-found treatment (ACT2-589).
  if (sessionPending) {
    return (
      <div className="flex flex-1 items-center justify-center" role="status" aria-label="Loading chat">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (sessionError) {
    // NOT_FOUND (no such chat) and FORBIDDEN (exists, not shared with you) get the same screen — the
    // reader can act on neither, and not distinguishing them keeps the UI from confirming that a
    // chat exists at an id they can't open.
    const unreachable = isOrpcNotFoundError(sessionError) || isOrpcForbiddenError(sessionError);
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Empty className="max-w-md border-0">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{unreachable ? 'Chat not found' : 'Could not load chat'}</EmptyTitle>
            <EmptyDescription>
              {unreachable
                ? 'This chat no longer exists, was never shared with you, or the link may be incorrect.'
                : 'Something went wrong while loading this chat. Try again in a moment.'}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button variant="secondary" asChild>
              <Link to={wp('/chats')}>Back to chats</Link>
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {session && (
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <h1 className="min-w-0 truncate text-sm font-semibold text-foreground">
            {session.title ?? 'New conversation'}
          </h1>
          <div className="flex shrink-0 items-center gap-2">
            <ChatArtifactsMenu
              artifacts={chatArtifacts}
              activeDashboardSlug={dashboardArtifactsStore.activeSlug}
              onDashboardSelect={handleDashboardArtifactOpen}
            />
            <ChatDownloadMenu
              chatId={session.id}
              disabledReason={sseMessages.some((m) => m.type === 'user_message') ? null : 'Nothing to download yet'}
            />
            {!readOnly && (
              <>
                <ChatSharePopover
                  visibility={visibility}
                  onUpdateVisibility={handleUpdateVisibility}
                  chatUrl={`${window.location.origin}/w/${encodeURIComponent(slug)}/chat/${session.id}`}
                  pending={visibilityMutation.isPending}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" aria-label="Chat actions" Icon={<EllipsisVertical />} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* An artifact-bound transcript is named by its own surface, and the server refuses a
                        rename of one. Disabled with the reason rather than hidden, per the UI rules. */}
                    <ActionTooltip
                      show={isArtifactBound}
                      message="This chat belongs to a dashboard, routine or audience, which names it."
                      className="block w-full"
                    >
                      <DropdownMenuItem disabled={isArtifactBound} onSelect={() => setRenamingId(sessionId)}>
                        <Pencil className="mr-2 size-3.5" />
                        Rename
                      </DropdownMenuItem>
                    </ActionTooltip>
                    <DropdownMenuItem variant="destructive" disabled={deleteMutation.isPending} onClick={handleDelete}>
                      <Trash2 className="mr-2 size-3.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      )}

      {canWrite && session && (
        <RenameChatDialog
          key={sessionId}
          open={renamingId === sessionId}
          onOpenChange={(next) => setRenamingId(next ? sessionId : null)}
          title={session.title}
          saving={renameMutation.isPending}
          onSave={(title) => renameMutation.mutateAsync({ id: sessionId, title })}
        />
      )}

      <DashboardPreviewMobileSheet
        open={panelOpen && isMobile}
        onOpenChange={handlePreviewOpenChange}
        store={dashboardArtifactsStore}
        chatSessionId={sessionId}
        readOnly={readOnly}
        onAskAssistant={readOnly ? undefined : (msg) => gate.gatedSend(msg)}
      />

      {/* Keep split layout mounted after first open so toggling the panel does not remount the composer. */}
      {useSplitLayout ? (
        <div className="flex min-h-0 flex-1">
          <DashboardPreviewSlideShell
            shown={previewSlide.shown}
            transcript={transcript}
            onClose={dashboardArtifactsStore.closePanel}
          >
            <ArtifactPanel
              store={dashboardArtifactsStore}
              chatSessionId={sessionId}
              visible={previewSlide.mounted}
              readOnly={readOnly}
              onAskAssistant={readOnly ? undefined : (msg) => gate.gatedSend(msg)}
            />
          </DashboardPreviewSlideShell>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{transcript}</div>
      )}
    </div>
  );
}
